import { useEffect, useState } from "react";
import { Trash2, Loader2, ShoppingBasket } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { api, errMsg } from "../lib/api";
import { useMarket } from "../lib/store";
import { useBasket, toOrders } from "../lib/basket";
import { useSubscribe, useTick } from "../lib/socket";
import { inr, fmt } from "../lib/format";

function Leg({ l, res }) {
  const { update, remove } = useBasket();
  const meta = useMarket((s) => s.meta[l.key]);
  const t = useTick(l.key);
  const sel = "h-7 px-1 rounded border border-input bg-background text-[11px]";
  return (
    <div className="border-b border-border/60 py-2 space-y-1.5" data-testid={`basket-leg-${meta?.trading_symbol || l.key}`}>
      <div className="flex items-center justify-between text-[12px]">
        <span><button onClick={() => update(l.id, { side: l.side === "BUY" ? "SELL" : "BUY" })}
          className={`text-[10px] font-bold rounded px-1.5 py-0.5 mr-1.5 ${l.side === "BUY" ? "bg-up/15 text-up" : "bg-down/15 text-down"}`}>{l.side}</button>
          <span className="font-medium">{meta?.trading_symbol || l.key}</span></span>
        <span className="flex items-center gap-2"><span className="num text-muted-foreground">{fmt(t?.ltp)}</span>
          <button onClick={() => remove(l.id)} data-testid="basket-leg-remove"><Trash2 className="h-3.5 w-3.5 text-muted-foreground hover:text-down" /></button></span>
      </div>
      <div className="flex items-center gap-1.5">
        <input type="number" min="1" value={l.lots} onChange={(e) => update(l.id, { lots: e.target.value })} className={`${sel} w-14 num`} title="Lots" />
        <span className="text-[10px] text-muted-foreground">lots × {meta?.lot_size || 1}</span>
        <select value={l.product} onChange={(e) => update(l.id, { product: e.target.value })} className={sel}><option>NRML</option><option>MIS</option></select>
        <select value={l.order_type} onChange={(e) => update(l.id, { order_type: e.target.value, price: t?.ltp || 0 })} className={sel}><option>MARKET</option><option>LIMIT</option></select>
        {l.order_type === "LIMIT" && <input type="number" value={l.price} onChange={(e) => update(l.id, { price: e.target.value })} className={`${sel} w-20 num`} />}
      </div>
      {res && !res.ok && <div className="text-[10.5px] text-down">{res.error}</div>}
    </div>
  );
}

export function BasketPanel() {
  const { legs, open, setOpen, clear } = useBasket();
  const meta = useMarket((s) => s.meta);
  const [pv, setPv] = useState(null);
  const [busy, setBusy] = useState(false);
  useSubscribe(legs.map((l) => l.key));
  const sig = JSON.stringify(toOrders(legs, meta));
  useEffect(() => {
    if (!open || !legs.length) { setPv(null); return undefined; }
    const t = setTimeout(() => api.post("/fno/basket/preview", { orders: JSON.parse(sig) }).then((r) => setPv(r.data)).catch(() => {}), 350);
    return () => clearTimeout(t);
  }, [sig, open, legs.length]);
  const place = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/fno/basket/place", { orders: JSON.parse(sig) });
      const rej = data.filter((o) => o.status === "REJECTED").length;
      toast[rej ? "error" : "success"](`Simulated order · basket placed: ${data.length - rej} accepted${rej ? `, ${rej} rejected` : ""}`);
      if (!rej) { clear(); setOpen(false); }
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent className="bg-card w-full sm:max-w-md flex flex-col" data-testid="basket-panel">
        <SheetHeader><SheetTitle className="font-display flex items-center gap-2"><ShoppingBasket className="h-4 w-4" />Basket ({legs.length}/20)</SheetTitle></SheetHeader>
        <div className="flex-1 overflow-auto thin-scroll mt-3">
          {!legs.length && <div className="text-[12px] text-muted-foreground py-8 text-center">Add legs from the option chain or strategy builder.</div>}
          {legs.map((l, i) => <Leg key={l.id} l={l} res={pv?.legs?.[i]} />)}
        </div>
        <div className="border-t border-border pt-3 space-y-1 text-[12px]">
          <div className="flex justify-between"><span className="text-muted-foreground">Required margin (sum of legs)</span><span className="num font-semibold" data-testid="basket-required-margin">{inr(pv?.required_margin)}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Charges</span><span className="num">{inr(pv?.charges)}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Available</span><span className="num">{inr(pv?.available_margin)}</span></div>
          <div className="flex gap-2 pt-2">
            <button onClick={clear} className="h-9 px-3 rounded-md border border-border text-[12px]" data-testid="basket-clear">Clear</button>
            <button disabled={busy || !legs.length} onClick={place} data-testid="basket-place-all" className="flex-1 h-9 rounded-md bg-primary text-primary-foreground font-semibold text-[13px] flex items-center justify-center gap-2 disabled:opacity-50">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}Place all orders</button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
